/**
 * OpenTelemetry Web SDK 計装（開発モード専用）
 * グローバルクリックリスナーでユーザー操作をspan化し、debug.jsonlへexport。
 * 本番時は環境変数 VITE_OTEL_EXPORTER でOTLP等に切り替え可能。
 */

import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { SimpleSpanProcessor, ConsoleSpanExporter, ReadableSpan } from '@opentelemetry/sdk-trace-base';
import { Resource } from '@opentelemetry/resources';
import { context, trace, Span, Context } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { propagation } from '@opentelemetry/api';

const SINV_DEBUG_API = (import.meta as any).env?.VITE_SINV_DEBUG_API || 'http://localhost:8000/api/__debug/error';
const OTEL_EXPORTER = (import.meta as any).env?.OTEL_EXPORTER || 'debug';

class DebugJsonlExporter {
  export(spans: ReadableSpan[], resultCallback: (result: { code: number }) => void) {
    const testId = (window as any).__SINV_E2E_TEST_ID as string | undefined;
    for (const span of spans) {
      const payload: Record<string, any> = {
        type: 'userAction',
        origin: 'frontend' as const,
        level: 'info' as const,
        traceId: span.spanContext().traceId,
        spanId: span.spanContext().spanId,
        parentSpanId: span.parentSpanId || undefined,
        name: span.name,
        target: span.attributes['ui.target'] as string || '',
        label: span.attributes['ui.label'] as string || '',
        page: span.attributes['ui.page'] as string || '',
        tagName: span.attributes['ui.tagName'] as string || '',
        duration: span.duration[0] * 1e3 + span.duration[1] / 1e6,
        timestamp: new Date(span.startTime[0] * 1e3 + span.startTime[1] / 1e6).toISOString(),
        attributes: Object.fromEntries(
          Object.entries(span.attributes).filter(([k]) => !k.startsWith('ui.'))
        ),
      };
      if (testId) payload.testId = testId;
      fetch(SINV_DEBUG_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }
    resultCallback({ code: 0 });
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

const resource = new Resource({
  'service.name': 'sinv-frontend',
});

const provider = new WebTracerProvider({ resource });

if (OTEL_EXPORTER === 'console') {
  provider.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()));
} else {
  provider.addSpanProcessor(new SimpleSpanProcessor(new DebugJsonlExporter() as any));
}

provider.register();
propagation.setGlobalPropagator(new W3CTraceContextPropagator());

const tracer = trace.getTracer('sinv-ui');

let _activeClickSpan: Span | null = null;
let _activeClickContext: Context | null = null;
let _pendingFetchCount = 0;

function getElementLabel(el: HTMLElement): string {
  if (el.getAttribute('aria-label')) return el.getAttribute('aria-label')!;
  if (el.innerText && el.innerText.length < 50) return el.innerText.trim();
  if (el.getAttribute('title')) return el.getAttribute('title')!;
  return '';
}

function getElementTarget(el: HTMLElement): string {
  const testId = el.getAttribute('data-testid');
  if (testId) return `[data-testid="${testId}"]`;
  const id = el.id;
  if (id) return `#${id}`;
  const tag = el.tagName.toLowerCase();
  const cls = el.className && typeof el.className === 'string'
    ? '.' + el.className.split(/\s+/).slice(0, 2).join('.')
    : '';
  return `${tag}${cls}`;
}

function endActiveSpan() {
  if (_activeClickSpan) {
    _activeClickSpan.end();
    _activeClickSpan = null;
    _activeClickContext = null;
  }
}

document.addEventListener('click', (event) => {
  const el = event.target as HTMLElement;
  if (!el) return;

  endActiveSpan();

  const span = tracer.startSpan('user.click', {
    attributes: {
      'ui.target': getElementTarget(el),
      'ui.label': getElementLabel(el),
      'ui.tagName': el.tagName.toLowerCase(),
      'ui.page': window.location.hash || window.location.pathname,
    },
  });

  _activeClickSpan = span;
  _activeClickContext = trace.setSpan(context.active(), span);
  _pendingFetchCount = 0;
}, { capture: true });

export function getActiveTraceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (_activeClickContext) {
    propagation.inject(_activeClickContext, headers);
    _pendingFetchCount++;
  }
  return headers;
}

export function notifyFetchComplete() {
  if (_pendingFetchCount > 0) {
    _pendingFetchCount--;
  }
  if (_pendingFetchCount <= 0 && _activeClickSpan) {
    setTimeout(() => {
      if (_pendingFetchCount <= 0) {
        endActiveSpan();
      }
    }, 100);
  }
}

let _currentView: string = '';

export function getCurrentView(): string {
  return _currentView;
}

export function setCurrentView(viewName: string, label?: string) {
  if (viewName === _currentView) return;
  _currentView = viewName;
  fetch(SINV_DEBUG_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: 'viewChange',
      origin: 'frontend',
      level: 'info',
      viewName,
      label: label || viewName,
      timestamp: new Date().toISOString(),
    }),
  }).catch(() => {});
}

export { tracer, provider };
